import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  pdpPopupTriggers,
  popupOnAddClickRedirectionFields,
} from '../../containers/HomePage/constants';
import { useScreenDetector } from '../custom-hooks/screen-detector';

import CentralCompactLayout from './CrossSell/Central/Layouts/Compact';
import CentralFullpageLayout from './CrossSell/Central/Layouts/Fullpage';
import CentralLineLayout from './CrossSell/Central/Layouts/Line';
import SlideoutLineLayout from './CrossSell/Slideout/Layouts/Line';
import SlideoutFullpageLayout from './CrossSell/Slideout/Layouts/Fullpage';

import CentralBundleLayout from './Bundle/Central/Layouts/Default';
import SlideoutBundleLayout from './Bundle/Slideout/Layouts/Default';

import {
  getSeparatedStyleValueArr,
  getCommonButtonWidthStyle,
  getDiscountCodeStyleFromTheme,
  getProductButtonStyleAndTextColorValue,
} from '../helpers/style';
import { addVariantsWithQuantityToCart } from '../../../common/cart';

// helpers
const addedVariantsPrefixMap = {
  'paaduks.myshopify.com': '',
  'ikeg-com-br.myshopify.com': '',
};

const getLayoutType = ({
  isMobile, // isMobile and isAdmin Mobile tab is already indentified in this new component
  popupMobileLayoutType,
  popupDesktopLayoutType,
}) => {
  if (isMobile) return popupMobileLayoutType;
  return popupDesktopLayoutType;
};
// helpers

export default function Popup(props) {
  const {
    isAdmin,
    isAdminMobileTab,
    mrOfferID,
    trigger,
    offerType,
    triggerType,
    primary,
    products,
    selectedVariantInfo,
    cart,
    offTextAdmin,
    productAdditions,
    isDiscountQuantityRestricted,
    discountQuantity,
    _getTotalQuantity,
    _getAddedVariantsToDisplay,
    _getProductAddtionsByProductIndex,
    _isMaximumProductsAdded,
    _getProductQuantity,
    _isPrimaryAndOfferedSame,
    onSelectVariant,
    goToProductPage,
    _onAddClick,
    addToCart: addToCartWrapper,
    isProductButtonActionGoToPage,
    isProductButtonActionCheckout,
    productEvent,
    eventConstants,
    isBottomBarButtonActionLoading,
    discountCodeWidgetProps = {},
    showCrossSellDiscountCodeWidget,
    showBundleDiscountCodeWidget,
    bundlePriceBarInfo,
    isOfferedProductPresentInCart, // used for bundle
    shopURL,
    isNumberOfOfferedProductsRestricted,
    isOfferedProductsRestrictedAndAdded,
    changeCartVartiantWithoutProperties,
    onProductImageClick,
    onProductTitleClick,
    offerLevelDiscountQuantity,
    atcStatus,
    variantsWithQuantityFromPdp,
    ...restProps // only rest props will be passed in the child components.
  } = props;
  const {
    popupType,
    popupMobileLayoutType,
    popupDesktopLayoutType,
    isVariantSelectionDisabled,
    isSelectedVariantItemsDisabled,

    // widget styles
    font: widgetFontFamily,
    offerBackgroundColor: widgetBackgroundColor,
    isOfferBordered: isWidgetBordered,
    offerBorderWidth: widgetBorderWidth,
    offerBorderColor: widgetBorderColor,
    widgetBorderRadius,
    widgetPadding,
    widgetMargin,
    widgetMobilePadding,
    widgetMobileMargin,
  } = props.theme;
  const { isMobile: isMobileDevice } = useScreenDetector();

  const isBundleOffer = offerType === 'bundle';

  const getIsStayOnSamePageAfterProductAddition = () =>
    popupOnAddClickRedirectionFields.every(field => !props[field]);

  const isMobile = useMemo(
    () => (isAdmin ? isAdminMobileTab : isMobileDevice),
    [isAdmin, isAdminMobileTab, isMobileDevice],
  );

  const layoutType = getLayoutType({
    isMobile,
    popupMobileLayoutType,
    popupDesktopLayoutType,
  });

  const getProductAddtionsByProductIndex = idx =>
    _getProductAddtionsByProductIndex({
      idx,
      products,
      productAdditions,
    });

  const productActionButtonStyles = getProductActionButtonStyles(
    props.theme,
    isMobile,
    isBundleOffer,
  );

  const getVariantFillerWidth = (variantFiller, variantFillerStyles) => {
    if (!variantFiller || !variantFillerStyles) return 0;

    const { type, fontFamily, fontWeight, padding } = variantFillerStyles;
    let { fontSize, mobileFontSize } = variantFillerStyles;

    if (Number.isNaN(Number(fontSize))) fontSize = 0;
    if (Number.isNaN(Number(mobileFontSize))) mobileFontSize = 0;

    const maxFontSize = Math.max(Number(fontSize), Number(mobileFontSize));

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    context.font = `${fontWeight} ${maxFontSize}px ${
      fontFamily === 'inherit'
        ? getComputedStyle(document.body).fontFamily
        : fontFamily
    }`;

    const textWidth = context.measureText(variantFiller).width;
    const paddingArr = getSeparatedStyleValueArr(padding);
    const borderWidth = type === 'button' ? 2 : 0;

    let fillerWidth =
      textWidth + Number(paddingArr[1]) + Number(paddingArr[3]) + borderWidth;

    // Adjusting addedToCartText width with iconWidth and gap
    if (type === 'text') {
      fillerWidth = fillerWidth + maxFontSize + 5;
    }

    return fillerWidth;
  };

  const getVariantFiller = useCallback(
    () => {
      const variantStyles = {
        type: 'button',
        fontSize: productActionButtonStyles.variantSelectorFontSize,
        mobileFontSize: productActionButtonStyles.variantSelectorMobileFontSize,
        fontFamily: productActionButtonStyles.variantSelectorFontFamily,
        fontWeight: productActionButtonStyles.variantSelectorFontWeight,
        fontStyle: productActionButtonStyles.variantSelectorFontStyle,
        padding: productActionButtonStyles.variantSelectorPadding,
        margin: productActionButtonStyles.variantSelectorMargin,
      };
      const atcStyles = {
        type: 'button',
        fontSize: productActionButtonStyles.atcButtonFontSize,
        mobileFontSize: productActionButtonStyles.atcButtonMobileFontSize,
        fontFamily: productActionButtonStyles.atcButtonFontFamily,
        fontWeight: productActionButtonStyles.atcButtonFontWeight,
        fontStyle: productActionButtonStyles.atcButtonFontStyle,
        padding: productActionButtonStyles.atcButtonPadding,
        margin: productActionButtonStyles.atcButtonMargin,
      };
      const addedStyles = {
        type: 'text',
        fontSize: productActionButtonStyles.addedToCartFontSize,
        mobileFontSize: productActionButtonStyles.addedToCartMobileFontSize,
        fontFamily: productActionButtonStyles.addedToCartFontFamily,
        fontWeight: productActionButtonStyles.addedToCartFontWeight,
        fontStyle: productActionButtonStyles.addedToCartFontStyle,
        padding: productActionButtonStyles.addedToCartPadding,
        margin: productActionButtonStyles.addedToCartMargin,
      };

      const fillerCandidates = [];

      let maxVaraintText = '';
      for (let i = 0; i < products.length; i += 1) {
        for (let j = 0; j < products[i].variants.length; j += 1) {
          if (
            maxVaraintText.length < products[i].variants[j].title.length &&
            products[i].variants[j].title !== 'Default Title'
          ) {
            maxVaraintText = products[i].variants[j].title;
          }
        }
      }
      fillerCandidates.push([
        maxVaraintText,
        {
          ...variantStyles,
          width: getVariantFillerWidth(maxVaraintText, variantStyles),
        },
      ]);

      fillerCandidates.push([
        props.theme.productButtonText,
        {
          ...atcStyles,
          width: getVariantFillerWidth(
            props.theme.productButtonText,
            atcStyles,
          ),
        },
      ]);

      if (isNumberOfOfferedProductsRestricted) {
        fillerCandidates.push([
          props.theme.addedToCartText,
          {
            ...addedStyles,
            width: getVariantFillerWidth(
              props.theme.addedToCartText,
              addedStyles,
            ),
          },
        ]);
      }

      let maxFillerWidth = 0;
      if (fillerCandidates.length !== 0) {
        maxFillerWidth = Math.max(
          ...fillerCandidates.map(candidate => candidate[1].width),
        );
      }

      return (
        fillerCandidates.find(
          candidate => candidate[1].width === maxFillerWidth,
        ) || []
      );
    },
    [popupMobileLayoutType, popupDesktopLayoutType, props.theme],
  );

  const [variantFiller, variantFillerStyles] = getVariantFiller();

  const getOfferLevelQuantityAdded = () => {
    let totalQuantity = productAdditions.reduce(
      (total, current) =>
        total +
        (current.quantity > discountQuantity && isDiscountQuantityRestricted
          ? discountQuantity
          : current.quantity),
      0,
    );

    if (_isPrimaryAndOfferedSame({ productAdditions, primary })) {
      totalQuantity -= 1;
    }

    return totalQuantity > offerLevelDiscountQuantity
      ? offerLevelDiscountQuantity
      : totalQuantity;
  };

  // Get all data for rendering and updating product items and variants
  const getProductAndSelectedVariantData = useCallback(
    (product, selectedVariant) => {
      if (!product.originalIndex) {
        product.originalIndex = products.findIndex(
          item =>
            item.id === product.id &&
            item.variants.some(variant => variant.id === selectedVariant.id),
        );
      }

      // get Selected Image for variant
      const images =
        product.images && product.images.length > 0
          ? product.images
          : [{ src: 'https://via.placeholder.com/124x164' }];

      // get current product's additions
      const currProductAdditions = getProductAddtionsByProductIndex(
        product.originalIndex,
      );

      // get current product's added quantity in cart
      let quantityAdded = _getProductQuantity({
        productAdditions: currProductAdditions,
      });

      // adjusting quantity if primary and offered(added in cart) are same
      // because one primary product should be present in cart
      if (
        _isPrimaryAndOfferedSame({
          productAdditions: currProductAdditions,
          primary,
        })
      ) {
        quantityAdded -= 1;
      }

      // get if discount doesn't apply/exist for selected variant
      const isDiscountDisabled = selectedVariant.calculatedDiscountFloat <= 0;

      const isBundlePrimary =
        isOfferedProductPresentInCart(product.originalIndex) && isBundleOffer;

      const offerLevelQuantityAdded = getOfferLevelQuantityAdded();

      const isProductLevelDiscountQuantityLimitReached =
        isDiscountQuantityRestricted && quantityAdded >= discountQuantity;

      const isOfferLevelDiscountQuantityLimitReached =
        isNumberOfOfferedProductsRestricted &&
        offerLevelQuantityAdded >= offerLevelDiscountQuantity;

      const isProductAdditionEnabled =
        !isOfferLevelDiscountQuantityLimitReached &&
        !isProductLevelDiscountQuantityLimitReached;

      // we double check isProductAdditionEnabled because in case when discountLevelQuantity is reached
      // and some quantity is added the because isProductAdditionEnabled gets false and atc button doesn't display
      // but we want to show it in added state therefore we use this condition

      const showATCButton =
        !(!isProductAdditionEnabled && !quantityAdded) ||
        offerType === 'upsell';

      // if offered product has multiple variants
      const hasMultipleVariants =
        product.variants.length > 0 &&
        product.variants[0].title !== 'Default Title';

      // is showVariantSelector for product
      const showVariantSelector =
        hasMultipleVariants &&
        !isVariantSelectionDisabled &&
        !(isBundleOffer && isBundlePrimary) &&
        !(
          isProductLevelDiscountQuantityLimitReached ||
          isOfferLevelDiscountQuantityLimitReached
        );

      // showSkipButton only for new fullpage layout
      const showSkipButton = layoutType === 'fullpage';

      // get added variants to show as chips
      const addedVariants = _getAddedVariantsToDisplay({
        productAdditions: currProductAdditions,
        primary,
      });

      const addedVariantsPrefix = addedVariantsPrefixMap[shopURL];

      // get if added variant chips are shown with the added quantity
      const showAddedVariantsChipQuantity =
        hasMultipleVariants &&
        !isSelectedVariantItemsDisabled &&
        discountQuantity > 1;

      // get if added variant chips are shown
      const showAddedVariantsChip =
        hasMultipleVariants &&
        !isSelectedVariantItemsDisabled &&
        quantityAdded > 0;

      const variantAtcStatus =
        atcStatus.spVariantID === selectedVariant.id
          ? atcStatus
          : {
              adding: false,
              added: false,
            };

      const isMaxQtyAdded =
        (isProductLevelDiscountQuantityLimitReached ||
          isOfferLevelDiscountQuantityLimitReached ||
          offerType === 'upsell') &&
        quantityAdded > 0;

      const handleVariantChange = e => {
        e.preventDefault();
        const selectedOption = e.target.value;
        let newSelectedVariant = {};

        for (let i = 0; i < product.variants.length; i += 1) {
          if (product.variants[i].title === selectedOption) {
            newSelectedVariant = product.variants[i];
            break;
          }
        }
        onSelectVariant(product.originalIndex, newSelectedVariant, true);
      };

      const onAddClick = async () => {
        if (
          pdpPopupTriggers.includes(trigger) &&
          !getIsStayOnSamePageAfterProductAddition()
        ) {
          await addVariantsWithQuantityToCart({
            variantsWithQuantity: variantsWithQuantityFromPdp,
            isSkipIntercept: true,
          });
        }
        _onAddClick({
          product,
          selectedVariant,
          isProductButtonActionGoToPage,
          goToProductPage,
          hasMultipleVariants,
          addToCart: addToCartWrapper,
          getProductQuantity: () =>
            _getProductQuantity({ productAdditions: currProductAdditions }),
          productEvent,
        });
      };

      return {
        images,
        offerPrice: selectedVariant.offer_price,
        price: selectedVariant.price,
        off: selectedVariant.off,
        isDiscountDisabled,
        quantityAdded,
        hasMultipleVariants,
        showVariantSelector,
        showATCButton,
        showSkipButton,
        addedVariants,
        addedVariantsPrefix,
        showAddedVariantsChip,
        showAddedVariantsChipQuantity,
        withoutFirstAction: isVariantSelectionDisabled, // this means first action (VS) is hidden by user.
        variantFiller,
        variantFillerStyles,
        variantOptions: product.variants.map(variant => variant.title),
        productAdditions: currProductAdditions,
        onProductImageClick: () => onProductImageClick(product.originalIndex),
        onProductTitleClick: () => onProductTitleClick(product.originalIndex),
        handleVariantChange,
        onAddClick,
        onSkipClick: restProps.onSkipClick,
        isProductAdditionEnabled,
        isOfferedProductsRestrictedAndAdded,
        variantAtcStatus,
        isMaxQtyAdded,
        // bundle
        isBundlePrimary,
      };
    },
    [
      JSON.stringify(selectedVariantInfo),
      JSON.stringify(productAdditions),
      isMobile,
      atcStatus,
    ],
  );

  const isShowBaseProduct = triggerType === 'product' && layoutType === 'line';

  const embeddedWidgetStyles = {
    fontFamily: widgetFontFamily,
    backgroundColor: widgetBackgroundColor,
    border:
      isWidgetBordered && `${widgetBorderWidth}px solid ${widgetBorderColor}`,
    borderRadius: widgetBorderRadius,
    padding: isMobile ? widgetMobilePadding : widgetPadding,
    margin: isMobile ? widgetMobileMargin : widgetMargin,
    mobilePadding: widgetMobilePadding,
    mobileMargin: widgetMobileMargin,
  };

  const popupOfferHeaderStyles = getPopupOfferHeaderStyles(isMobile, props);
  const popupOfferBottomBarStyles = getPopupOfferBottomBarStyles(
    props.theme,
    isMobile,
    isBundleOffer,
  );

  const productImageDimensions = getProductImageDimensionValues(props.theme);

  const discountCodeStyles = getDiscountCodeStyleFromTheme(props.theme);

  const commonProps = {
    isMobile,
    primary,
    products,
    selectedVariantInfo,
    embeddedWidgetStyles,
    productActionButtonStyles,
    getProductAndSelectedVariantData,
    popupOfferHeaderStyles,
    popupOfferBottomBarStyles,
    priceDetailStyles: getPriceDetailStyles(isMobile, props.theme),
    productImageDimensions,
    showDiscountCode: isBundleOffer
      ? showBundleDiscountCodeWidget
      : showCrossSellDiscountCodeWidget &&
        popupType === 'slideout' &&
        layoutType === 'compact',
    discountCodeProps: {
      discountCodeStyles,
      ...discountCodeWidgetProps,
    },
    atcStatus,
    isBottomBarButtonActionLoading,
  };

  const crossSellProps = {
    isShowBaseProduct,
    offerLevelQuantityAdded: getOfferLevelQuantityAdded(),
    offerLevelDiscountQuantity,
    ...commonProps,
    ...restProps,
  };

  const bundleProps = {
    bundlePriceBarInfo,
    ...commonProps,
    ...restProps,
  };

  if (offerType === 'crossell' || offerType === 'upsell') {
    if (popupType === 'central' && !isMobile) {
      if (layoutType === 'compact') {
        return <CentralCompactLayout {...crossSellProps} />;
      }
      if (layoutType === 'line') {
        return <CentralLineLayout {...crossSellProps} />;
      }
      if (layoutType === 'fullpage') {
        return <CentralFullpageLayout {...crossSellProps} />;
      }
    } else if (
      popupType === 'slideout' ||
      (popupType === 'central' && isMobile)
    ) {
      // no difference between line and compact layout in slideout
      if (layoutType === 'compact') {
        return <SlideoutLineLayout {...crossSellProps} />;
      }
      if (layoutType === 'line') {
        return <SlideoutLineLayout {...crossSellProps} />;
      }
      if (layoutType === 'fullpage') {
        return <SlideoutFullpageLayout {...crossSellProps} />;
      }
    }
  }

  if (isBundleOffer) {
    if (popupType === 'central' && !isMobile) {
      return <CentralBundleLayout {...bundleProps} />;
    }
    if (popupType === 'slideout' || (popupType === 'central' && isMobile)) {
      return <SlideoutBundleLayout {...bundleProps} />;
    }
  }

  return 'Could not render mr popup widget';
}

Popup.propTypes = {
  isAdmin: PropTypes.bool,
  isAdminMobileTab: PropTypes.bool,
  offerType: PropTypes.string,
  theme: PropTypes.object,
  mrOfferID: PropTypes.string,
  trigger: PropTypes.string,
  triggerType: PropTypes.string,
  primary: PropTypes.object,
  products: PropTypes.array,
  selectedVariantInfo: PropTypes.array,
  cart: PropTypes.array,
  offTextAdmin: PropTypes.string,
  productAdditions: PropTypes.array,
  isDiscountQuantityRestricted: PropTypes.bool,
  discountQuantity: PropTypes.number,
  _getTotalQuantity: PropTypes.func,
  _getAddedVariantsToDisplay: PropTypes.func,
  _getProductAddtionsByProductIndex: PropTypes.func,
  _isMaximumProductsAdded: PropTypes.func,
  _getProductQuantity: PropTypes.func,
  _isPrimaryAndOfferedSame: PropTypes.func,
  onSelectVariant: PropTypes.func,
  goToProductPage: PropTypes.func,
  _onAddClick: PropTypes.func,
  addToCart: PropTypes.func,
  isProductButtonActionGoToPage: PropTypes.bool,
  isProductButtonActionCheckout: PropTypes.bool,
  isProductButtonActionGoToCart: PropTypes.bool,
  productEvent: PropTypes.func,
  eventConstants: PropTypes.object,
  isBottomBarButtonActionLoading: PropTypes.bool,
  discountCodeWidgetProps: PropTypes.object,
  showCrossSellDiscountCodeWidget: PropTypes.bool,
  showBundleDiscountCodeWidget: PropTypes.bool,
  bundlePriceBarInfo: PropTypes.object,
  isOfferedProductPresentInCart: PropTypes.func, // used for bundle
  shopURL: PropTypes.string,
  isNumberOfOfferedProductsRestricted: PropTypes.bool,
  isOfferedProductsRestrictedAndAdded: PropTypes.bool,
  changeCartVartiantWithoutProperties: PropTypes.func,
  onProductImageClick: PropTypes.func,
  onProductTitleClick: PropTypes.func,
  offerLevelDiscountQuantity: PropTypes.number,
  atcStatus: PropTypes.string,
  variantsWithQuantityFromPdp: PropTypes.array,
};

const getProductActionButtonStyles = (theme, isMobile, isBundleOffer) => {
  const { popupType, popupMobileLayoutType, popupDesktopLayoutType } = theme;

  let commonWidthStyle = getCommonButtonWidthStyle(
    theme.productButtonWidthStyle,
    theme.productVariantWidthStyle,
  );

  if (popupType === 'central' && popupDesktopLayoutType === 'fullpage') {
    commonWidthStyle = getCommonButtonWidthStyle(theme.productButtonWidthStyle);
  }

  if (
    (popupType === 'slideout' && popupDesktopLayoutType === 'fullpage') ||
    (isMobile && popupMobileLayoutType === 'fullpage')
  ) {
    commonWidthStyle = getCommonButtonWidthStyle(
      theme.productButtonWidthStyle,
      isMobile
        ? theme.bottomBarSecondaryButtonMobileWidthStyle
        : theme.bottomBarSecondaryButtonWidthStyle,
    );
  }

  const { styleValue, textColorValue } = getProductButtonStyleAndTextColorValue(
    {
      theme,
      isEmbedded: false,
      isBundleOffer,
    },
  );

  return {
    atcButtonText: theme.productButtonText,
    atcButtonTextColor: textColorValue,
    atcButtonStyle: styleValue,
    atcButtonBackgroundColor: theme.productButtonColor,
    atcButtonBorderColor: theme.productButtonColor,
    atcButtonBorderRadius: theme.productButtonBorderRadius,
    atcButtonFontSize: isMobile
      ? theme.productButtonMobileFontSize
      : theme.productButtonFontSize,
    atcButtonMobileFontSize: theme.productButtonMobileFontSize,
    atcButtonFontStyle: theme.productButtonFontStyle,
    atcButtonFontWeight: theme.productButtonFontWeight,
    atcButtonTextDecoration: theme.productButtonTextDecoration,
    atcButtonTextAlignment: theme.productButtonTextAlignment,
    atcButtonFontFamily: theme.productButtonFontFamily,
    atcButtonPadding: theme.productButtonPadding,
    atcButtonMargin: theme.productButtonMargin,
    atcButtonHeight: isMobile
      ? theme.productButtonMobileHeight
      : theme.productButtonHeight,
    atcButtonWidth: isMobile
      ? theme.productButtonMobileWidth
      : theme.productButtonWidth,
    atcButtonWidthStyle: commonWidthStyle,
    atcProductAddingText: theme.atcProductAddingText,
    atcProductAddedText: theme.atcProductAddedText,
    quantitySelectorTextColor: theme.quantityButtonTextColor,
    quantitySelectorStyle: theme.quantityButtonStyle,
    quantitySelectorBackgroundColor: theme.quantityButtonColor,
    quantitySelectorBorderColor: theme.quantityButtonColor,
    quantitySelectorBorderRadius: theme.quantityButtonBorderRadius,
    quantitySelectorFontSize: isMobile
      ? theme.quantityButtonMobileFontSize
      : theme.quantityButtonFontSize,
    quantitySelectorMobileFontSize: theme.quantityButtonMobileFontSize,
    quantitySelectorFontStyle: theme.quantityButtonFontStyle,
    quantitySelectorFontWeight: theme.quantityButtonFontWeight,
    quantitySelectorTextDecoration: theme.quantityButtonTextDecoration,
    quantitySelectorTextAlignment: theme.quantityButtonTextAlignment,
    quantitySelectorFontFamily: theme.quantityButtonFontFamily,
    quantitySelectorPadding: theme.quantityButtonPadding,
    quantitySelectorMargin: theme.quantityButtonMargin,
    quantitySelectorHeight: isMobile
      ? theme.quantityButtonMobileHeight
      : theme.quantityButtonHeight,
    // quantitySelectorWidth: isMobile
    //   ? theme.quantityButtonMobileWidth
    //   : theme.quantityButtonWidth,
    // quantitySelectorWidthStyle: theme.quantityButtonWidthStyle,
    variantSelectorTextColor: theme.productVariantTextColor,
    variantSelectorStyle: theme.productVariantStyle,
    variantSelectorBackgroundColor: theme.productVariantColor,
    variantSelectorBorderColor: theme.productVariantColor,
    variantSelectorBorderRadius: theme.productVariantBorderRadius,
    variantSelectorFontSize: isMobile
      ? theme.variantDropdownMobileFontSize
      : theme.variantDropdownFontSize,
    variantSelectorMobileFontSize: theme.variantDropdownMobileFontSize,
    variantSelectorFontStyle: theme.productVariantFontStyle,
    variantSelectorFontWeight: theme.productVariantFontWeight,
    variantSelectorTextDecoration: theme.productVariantTextDecoration,
    variantSelectorTextAlignment: isMobile
      ? theme.productVariantMobileTextAlignment
      : theme.productVariantTextAlignment,
    variantSelectorFontFamily: theme.productVariantFontFamily,
    variantSelectorPadding: isMobile
      ? theme.productVariantMobilePadding
      : theme.productVariantPadding,
    variantSelectorMargin: isMobile
      ? theme.productVariantMobileMargin
      : theme.productVariantMargin,
    variantSelectorHeight: isMobile
      ? theme.productVariantMobileHeight
      : theme.productVariantHeight,
    variantSelectorWidth: isMobile
      ? theme.productVariantMobileWidth
      : theme.productVariantWidth,
    variantSelectorWidthStyle: commonWidthStyle,
    addedToCartText: theme.addedToCartText,
    addedToCartTextColor: theme.addedToCartTextColor,
    addedToCartFontSize: isMobile
      ? theme.addedToCartMobileFontSize
      : theme.addedToCartFontSize,
    addedToCartMobileFontSize: theme.addedToCartMobileFontSize,
    addedToCartFontStyle: theme.addedToCartFontStyle,
    addedToCartFontWeight: theme.addedToCartFontWeight,
    addedToCartTextDecoration: theme.addedToCartTextDecoration,
    addedToCartTextAlignment: theme.addedToCartTextAlignment,
    addedToCartFontFamily: theme.addedToCartFontFamily,
    addedToCartPadding: theme.addedToCartPadding,
    addedToCartMargin: theme.addedToCartMargin,

    skipButtonText: theme.bottomBarSecondaryButtonText,
    skipButtonTextColor: theme.bottomBarSecondaryButtonTextColor,
    skipButtonStyle: isMobile
      ? theme.bottomBarSecondaryButtonMobileStyle
      : theme.bottomBarSecondaryButtonStyle,
    skipButtonBackgroundColor: theme.bottomBarSecondaryButtonColor,
    skipButtonBorderColor: theme.bottomBarSecondaryButtonColor,
    skipButtonBorderRadius: theme.bottomBarSecondaryButtonBorderRadius,
    skipButtonFontSize: isMobile
      ? theme.bottomBarSecondaryButtonMobileFontSize
      : theme.bottomBarSecondaryButtonFontSize,
    skipButtonMobileFontSize: theme.bottomBarSecondaryButtonMobileFontSize,
    skipButtonFontStyle: theme.bottomBarSecondaryButtonFontStyle,
    skipButtonFontWeight: theme.bottomBarSecondaryButtonFontWeight,
    skipButtonTextDecoration: theme.bottomBarSecondaryButtonTextDecoration,
    skipButtonTextAlignment: theme.bottomBarSecondaryButtonTextAlignment,
    skipButtonFontFamily: theme.bottomBarSecondaryButtonFontFamily,
    skipButtonPadding: theme.bottomBarSecondaryButtonPadding,
    skipButtonMargin: theme.bottomBarSecondaryButtonMargin,
    skipButtonHeight: isMobile
      ? theme.bottomBarSecondaryButtonMobileHeight
      : theme.bottomBarSecondaryButtonHeight,
    skipButtonWidth: isMobile
      ? theme.bottomBarSecondaryButtonMobileWidth
      : theme.bottomBarSecondaryButtonWidth,
    skipButtonWidthStyle: commonWidthStyle,
  };
};

const getPopupOfferHeaderStyles = (
  isMobile,
  { isTimerDisabled, seconds, minutes, theme },
) => ({
  offerHeaderContainerBackgroundColor: theme.topBarBackgroundColor,
  offerHeaderContainerPadding: isMobile
    ? theme.offerHeaderContainerMobilePadding
    : theme.offerHeaderContainerPadding,
  offerHeaderContainerMargin: isMobile
    ? theme.offerHeaderContainerMobileMargin
    : theme.offerHeaderContainerMargin,
  offerHeaderText: theme.offer_header_text,
  offerHeaderFontSize: isMobile
    ? theme.offerHeaderMobileFontSize
    : theme.offerHeaderFontSize,
  offerHeaderMobileFontSize: theme.offerHeaderMobileFontSize,
  offerHeaderFontStyle: theme.offerHeaderFontStyle,
  offerHeaderFontWeight: theme.offerHeaderFontWeight,
  offerHeaderTextColor: theme.offerHeaderTextColor,
  offerHeaderTextDecoration: theme.offerHeaderTextDecoration,
  offerHeaderFontFamily: theme.offerHeaderFontFamily,
  offerHeaderTextAlignment: theme.offerHeaderTextAlignment,
  offerHeaderPadding: isMobile
    ? theme.offerHeaderMobilePadding
    : theme.offerHeaderPadding,
  offerHeaderMargin: isMobile
    ? theme.offerHeaderMobileMargin
    : theme.offerHeaderMargin,
  offerHeaderMobilePadding: theme.offerHeaderMobilePadding,
  offerSubtitleText: theme.offerSubtitleText,
  offerSubtitleFontSize: isMobile
    ? theme.offerSubtitleMobileFontSize
    : theme.offerSubtitleFontSize,
  offerSubtitleMobileFontSize: theme.offerSubtitleMobileFontSize,
  offerSubtitleFontStyle: theme.offerSubtitleFontStyle,
  offerSubtitleFontWeight: theme.offerSubtitleFontWeight,
  offerSubtitleTextColor: theme.offerSubtitleTextColor,
  offerSubtitleTextDecoration: theme.offerSubtitleTextDecoration,
  offerSubtitleFontFamily: theme.offerSubtitleFontFamily,
  offerSubtitleTextAlignment: theme.offerSubtitleTextAlignment,
  offerSubtitlePadding: isMobile
    ? theme.offerSubtitleMobilePadding
    : theme.offerSubtitlePadding,
  offerSubtitleMargin: isMobile
    ? theme.offerSubtitleMobileMargin
    : theme.offerSubtitleMargin,
  isTimerDisabled,
  seconds,
  minutes,
  offerTimerText: theme.offerTimerText,
  timerColor: theme.topBarTimerColor,
  timerTextColor: theme.topBarTimerTextColor,
  timerTitleText: theme.offerTitleText,
  timerTitleFontSize: isMobile
    ? theme.offerTitleMobileFontSize
    : theme.offerTitleFontSize,
  timerTitleMobileFontSize: theme.offerTitleMobileFontSize,
  timerTitleTextColor: theme.offerTitleTextColor,
  timerTitleFontWeight: theme.offerTitleFontWeight,
  timerTitleFontStyle: theme.offerTitleFontStyle,
  timerTitleTextDecoration: theme.offerTitleTextDecoration,
  timerTitleTextAlignment: theme.offerTitleTextAlignment,
  timerTitleFontFamily: theme.offerTitleFontFamily,
  timerTitlePadding: isMobile
    ? theme.offerTitleMobilePadding
    : theme.offerTitlePadding,
  timerTitleMargin: isMobile
    ? theme.offerTitleMobileMargin
    : theme.offerTitleMargin,
  isPopupCloseButtonEnabled: theme.isPopupCloseButtonEnabled,
});

const getPriceDetailStyles = (isMobile, theme) => {
  const {
    popupType,
    productPriceFontSize,
    productPriceMobileFontSize,
    productDiscountFontSize,
    productPricePopupCentralFontSize,
    productDiscountPopupCentralFontSize,
  } = theme;

  let priceFontSize = productPriceFontSize;
  let priceMobileFontSize = productPriceMobileFontSize;
  let discountFontSize = productDiscountFontSize;

  if (popupType === 'central' && !isMobile) {
    priceFontSize = productPricePopupCentralFontSize;
    priceMobileFontSize = productPricePopupCentralFontSize;
    discountFontSize = productDiscountPopupCentralFontSize;
  }

  return {
    offerPriceStyles: {
      color: theme.productPriceTextColor,
      fontSize: isMobile ? priceMobileFontSize : priceFontSize,
      mobileFontSize: priceMobileFontSize,
      fontStyle: theme.productPriceFontStyle,
      fontWeight: theme.productPriceFontWeight,
      textDecoration: theme.productPriceTextDecoration,
    },
    priceStyles: {
      color: theme.productPriceTextColor,
      fontSize: isMobile ? priceMobileFontSize : priceFontSize,
      mobileFontSize: priceMobileFontSize,
      fontStyle: 'normal',
      fontWeight: 'normal',
      textDecoration: 'none',
    },
    offStyles: {
      color: theme.productDiscountTextColor,
      fontSize: isMobile
        ? theme.productDiscountMobileFontSize
        : discountFontSize,
      mobileFontSize: theme.productDiscountMobileFontSize,
      fontStyle: theme.productDiscountFontStyle,
      fontWeight: theme.productDiscountFontWeight,
      textDecoration: theme.productDiscountTextDecoration,
    },
  };
};

const getPopupOfferBottomBarStyles = (theme, isMobile, isBundleOffer) => {
  const commonWidthStyle = getCommonButtonWidthStyle(
    theme.bottomBarButtonWidthStyle,
    isMobile
      ? theme.bottomBarSecondaryButtonMobileWidthStyle
      : theme.bottomBarSecondaryButtonWidthStyle,
  );

  const result = {
    bottomBarContainerBackgroundColor: theme.offerBackgroundColor,
    bottomBarContainerPadding: isMobile
      ? theme.bottomBarContainerMobilePadding
      : theme.bottomBarContainerPadding,
    bottomBarContainerMargin: isMobile
      ? theme.bottomBarContainerMobileMargin
      : theme.bottomBarContainerMargin,
    bottomBarPrimaryButtonText: isBundleOffer
      ? theme.bottomBarBundleButtonText
      : theme.bottomBarButtonText,
    bottomBarPrimaryButtonTextColor: theme.bottomBarButtonTextColor,
    bottomBarPrimaryButtonStyle: theme.bottomBarButtonStyle,
    bottomBarPrimaryButtonBackgroundColor: theme.bottomBarButtonColor,
    bottomBarPrimaryButtonBorderColor: theme.bottomBarButtonColor,
    bottomBarPrimaryButtonBorderRadius: theme.bottomBarButtonBorderRadius,
    bottomBarPrimaryButtonFontSize: isMobile
      ? theme.bottomBarButtonMobileFontSize
      : theme.bottomBarButtonFontSize,
    bottomBarPrimaryButtonMobileFontSize: theme.bottomBarButtonMobileFontSize,
    bottomBarPrimaryButtonFontStyle: theme.bottomBarButtonFontStyle,
    bottomBarPrimaryButtonFontWeight: theme.bottomBarButtonFontWeight,
    bottomBarPrimaryButtonTextDecoration: theme.bottomBarButtonTextDecoration,
    bottomBarPrimaryButtonTextAlignment: theme.bottomBarButtonTextAlignment,
    bottomBarPrimaryButtonFontFamily: theme.bottomBarButtonFontFamily,
    bottomBarPrimaryButtonPadding: theme.bottomBarButtonPadding,
    bottomBarPrimaryButtonMargin: theme.bottomBarButtonMargin,
    bottomBarPrimaryButtonHeight: isMobile
      ? theme.bottomBarButtonMobileHeight
      : theme.bottomBarButtonHeight,
    bottomBarPrimaryButtonWidth: isMobile
      ? theme.bottomBarButtonMobileWidth
      : theme.bottomBarButtonWidth,
    bottomBarPrimaryButtonWidthStyle: commonWidthStyle,
    bottomBarSecondaryButtonText: theme.bottomBarSecondaryButtonText,
    bottomBarSecondaryButtonTextColor: theme.bottomBarSecondaryButtonTextColor,
    bottomBarSecondaryButtonStyle: isMobile
      ? theme.bottomBarSecondaryButtonMobileStyle
      : theme.bottomBarSecondaryButtonStyle,
    bottomBarSecondaryButtonBackgroundColor:
      theme.bottomBarSecondaryButtonColor,
    bottomBarSecondaryButtonBorderColor: theme.bottomBarSecondaryButtonColor,
    bottomBarSecondaryButtonBorderRadius:
      theme.bottomBarSecondaryButtonBorderRadius,
    bottomBarSecondaryButtonFontSize: isMobile
      ? theme.bottomBarSecondaryButtonMobileFontSize
      : theme.bottomBarSecondaryButtonFontSize,
    bottomBarSecondaryButtonMobileFontSize:
      theme.bottomBarSecondaryButtonMobileFontSize,
    bottomBarSecondaryButtonFontStyle: theme.bottomBarSecondaryButtonFontStyle,
    bottomBarSecondaryButtonFontWeight:
      theme.bottomBarSecondaryButtonFontWeight,
    bottomBarSecondaryButtonTextDecoration:
      theme.bottomBarSecondaryButtonTextDecoration,
    bottomBarSecondaryButtonTextAlignment:
      theme.bottomBarSecondaryButtonTextAlignment,
    bottomBarSecondaryButtonFontFamily:
      theme.bottomBarSecondaryButtonFontFamily,
    bottomBarSecondaryButtonPadding: theme.bottomBarSecondaryButtonPadding,
    bottomBarSecondaryButtonMargin: theme.bottomBarSecondaryButtonMargin,
    bottomBarSecondaryButtonHeight: isMobile
      ? theme.bottomBarSecondaryButtonMobileHeight
      : theme.bottomBarSecondaryButtonHeight,
    bottomBarSecondaryButtonWidth: isMobile
      ? theme.bottomBarSecondaryButtonMobileWidth
      : theme.bottomBarSecondaryButtonWidth,
    bottomBarSecondaryButtonWidthStyle: commonWidthStyle,
    bundlePriceBarBackgroundColor: theme.bundlePriceBarBackgroundColor,
    bundlePriceBarBorderRadius: theme.bundlePriceBarBorderRadius,
    bundleTaglineTextColor: theme.bundleTaglineTextColor,
    bundleTaglineFontSize: isMobile
      ? theme.bundleTaglineMobileFontSize
      : theme.bundleTaglineFontSize,
    bundleTaglineMobileFontSize: theme.bundleTaglineMobileFontSize,
    bundleTaglineFontStyle: theme.bundleTaglineFontStyle,
    bundleTaglineFontWeight: theme.bundleTaglineFontWeight,
    bundleTaglineTextDecoration: theme.bundleTaglineTextDecoration,
    bundleTaglineTextAlignment: theme.bundleTaglineTextAlignment,
    bundleTaglineFontFamily: theme.bundleTaglineFontFamily,
    bundleTaglinePadding: theme.bundleTaglinePadding,
    bundleTaglineMargin: theme.bundleTaglineMargin,
    carouselActionColor: theme.productButtonColor,
  };

  return result;
};

const getProductImageDimensionValues = theme => {
  const {
    popupType,
    popupDesktopLayoutType,
    popupMobileLayoutType,
    productImageWidth,
    productImageHeight,
    productImageMobileWidth,
    productImageMobileHeight,
    productImagePopupSlideoutFullpageWidth,
    productImagePopupSlideoutFullpageHeight,
    productImagePopupSlideoutFullpageMobileWidth,
    productImagePopupSlideoutFullpageMobileHeight,
    productImagePopupCentralFullpageWidth,
    productImagePopupCentralFullpageHeight,
    productImagePopupCentralFullpageMobileWidth,
    productImagePopupCentralFullpageMobileHeight,
    productImagePopupSlideoutLineWidth,
    productImagePopupSlideoutLineHeight,
    productImagePopupSlideoutLineMobileWidth,
    productImagePopupSlideoutLineMobileHeight,
    productImagePopupCentralLineWidth,
    productImagePopupCentralLineHeight,
    productImagePopupCentralLineMobileWidth,
    productImagePopupCentralLineMobileHeight,
    productImagePopupCentralCompactWidth,
    productImagePopupCentralCompactHeight,
  } = theme;

  const out = {};
  if (popupType === 'slideout') {
    if (popupDesktopLayoutType === 'compact') {
      out.width = productImageWidth;
      out.height = productImageHeight;
    } else if (popupDesktopLayoutType === 'fullpage') {
      out.width = productImagePopupSlideoutFullpageWidth;
      out.height = productImagePopupSlideoutFullpageHeight;
    } else {
      out.width = productImagePopupSlideoutLineWidth;
      out.height = productImagePopupSlideoutLineHeight;
    }

    if (popupMobileLayoutType === 'compact') {
      out.mobileWidth = productImageMobileWidth;
      out.mobileHeight = productImageMobileHeight;
    } else if (popupMobileLayoutType === 'fullpage') {
      out.mobileWidth = productImagePopupSlideoutFullpageMobileWidth;
      out.mobileHeight = productImagePopupSlideoutFullpageMobileHeight;
    } else {
      out.mobileWidth = productImagePopupSlideoutLineMobileWidth;
      out.mobileHeight = productImagePopupSlideoutLineMobileHeight;
    }
  } else {
    if (popupDesktopLayoutType === 'compact') {
      out.width = productImagePopupCentralCompactWidth;
      out.height = productImagePopupCentralCompactHeight;
    } else if (popupDesktopLayoutType === 'fullpage') {
      out.width = productImagePopupCentralFullpageWidth;
      out.height = productImagePopupCentralFullpageHeight;
    } else {
      out.width = productImagePopupCentralLineWidth;
      out.height = productImagePopupCentralLineHeight;
    }

    if (popupMobileLayoutType === 'compact') {
      out.mobileWidth = productImageMobileWidth;
      out.mobileHeight = productImageMobileHeight;
    } else if (popupMobileLayoutType === 'fullpage') {
      out.mobileWidth = productImagePopupCentralFullpageMobileWidth;
      out.mobileHeight = productImagePopupCentralFullpageMobileHeight;
    } else {
      out.mobileWidth = productImagePopupCentralLineMobileWidth;
      out.mobileHeight = productImagePopupCentralLineMobileHeight;
    }
  }
  return out;
};
